import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MailAccountGetByOrganizationIdContract } from '@app-types/api/mail-account';
import { MailAccountClient } from '@app-services/api/clients/mail-account.client';
import { PermissionService } from '@app-services/permission/permission.service';
import { PermissionType } from '@app-types/enums/permission-type';
import { FilterFields, Options, OptionsEnum } from '@app-types/common';
import { MatDialog } from '@angular/material/dialog';
import { MobileObserverService } from '@app-services/adaptive/mobile-observer.service';
import { OrganizationSettingsClient } from 'src/app/services/api/clients/organization-settings.client';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { FormatMailAccountSearchOrders } from 'src/app/shared/pipes/format-mail-account-search-orders.pipe';
import { ProviderClient } from 'src/app/services/api/clients/provider.client';
import {
  MENU_STATE,
  SETTING_SETTINGS,
  defaultFilterFields,
} from '@app-shared/constants/constants';
import {
  CONNECTION_STATUS,
  CONNECTION_STATUS_INFO,
} from '@app-shared/constants/connection-status';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SearchBarComponent } from '@app-components/common/search-bar/search-bar.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { SomethingWentWrongComponent } from '@app-components/common/error/something-went-wrong/something-went-wrong.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_MENU_DEFAULT_OPTIONS,
  MatMenuModule,
} from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { DrawerService } from '@app-services/drawer.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AddMailAccountsToUsersFormComponent } from '@app-components/settings/organization/mail-account/add-mail-accounts-to-users-form/add-mail-accounts-to-users-form.component';
import { MailAccountCreateDialogComponent } from '@app-components/settings/organization/mail-account/mail-account-create-dialog/mail-account-create-dialog.component';
import { MatCardModule } from '@angular/material/card';
import { Subscription } from 'rxjs';
import { SkeletonTableComponent } from '@app-components/common/skeletons/skeleton-table/skeleton-table.component';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { InfinityScrollComponent } from '@app-components/common/infinity-scroll/infinity-scroll.component';
import { TableHeaderComponent } from '@app-components/common/table-header/table-header.component';
import { MailAccountSearchOrder } from '@app-types/enums/MailAccountSearchOrder';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
  BaseGetSearchCountByIdRequest,
} from '@app-types/base/base';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { OrderDirection } from '@app-types/enums/order-direction';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { PermissionNavTabHelper } from '@app-services/permission/permission-nav-tab-helper';
import { NgClass } from '@angular/common';

const { PAGE_SIZE, STEP_FETCH } = SETTING_SETTINGS;
const { ORGANIZATION, MAIL_ACCOUNT, GENERAL } = ROUTES;

@Component({
  selector: 'app-mail-account',
  templateUrl: './mail-account.component.html',
  styleUrls: ['./mail-account.component.scss'],
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    SearchBarComponent,
    NoDataComponent,
    SomethingWentWrongComponent,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    AddMailAccountsToUsersFormComponent,
    MailAccountCreateDialogComponent,
    RouterModule,
    MatCardModule,
    SkeletonTableComponent,
    InfinityScrollComponent,
    TableHeaderComponent,
    MatSort,
    MatSortModule,
    LoaderComponent,
    NgClass,
  ],
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'panelClassMenu' },
    },
  ],
})
export class MailAccountComponent implements OnInit, OnDestroy {
  @HostBinding('class') className = 'setting-container';
  public organizationId: number | undefined;
  public isLoading = true;
  isUploaded = false;
  public dataSource =
    new MatTableDataSource<MailAccountGetByOrganizationIdContract>([]);
  public displayedColumns: string[] = [
    'name',
    'license',
    'codeSia',
    'anagId',
    'email',
    'providerName',
    'emailState',
    'archive',
    'spaceUsedGB',
    'connect',
    'actions',
  ];
  public isError = false;
  public offset = 0;
  public stepFetch = STEP_FETCH;
  public pageSize = PAGE_SIZE;
  public totalSize = 0;
  public canNavigateForward = false;
  public isLoadingScroll = false;
  public isErrorScroll = false;
  public searchValue = '';
  public canManageMailAccounts = false;
  public canManageMailAccount = false;
  public useMobileView = false;
  public useTabletView = false;
  public iscredemISAMSupport = true;
  public spaceUsed = 0;
  public searchOrder: MailAccountSearchOrder | 0 = 0;
  public searchOrders: OptionsEnum[];
  public provider = -1;
  public filter: FilterFields<MailAccountSearchOrder> = {
    ...defaultFilterFields,
  };
  public providers: Options[] = [];
  connectionStatusName = CONNECTION_STATUS;
  connectionStatusInfo = CONNECTION_STATUS_INFO;
  menuConnectionStatus = Object.values(CONNECTION_STATUS);
  menuStateList = MENU_STATE;
  public isExporting = false;
  isReloadLoading = false;
  private suppressSortDrivenSearch = false;
  selectedMailAccount: MailAccountGetByOrganizationIdContract | null;
  private formatMailAccountSearchOrders = new FormatMailAccountSearchOrders();
  private useMobileViewSubscription: Subscription;
  private useTabletViewSubscription: Subscription;
  @ViewChild('createDrawer') createDrawer: TemplateRef<any>;
  @ViewChild('addMailAccountsToUsersDrawer')
  addMailAccountsToUsersDrawer: TemplateRef<any>;
  @ViewChild(InfinityScrollComponent) infinityScroll: InfinityScrollComponent;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public matchError: MatchError,
    protected router: Router,
    protected route: ActivatedRoute,
    private mailAccountClient: MailAccountClient,
    private permissionService: PermissionService,
    private organizationSettingsClient: OrganizationSettingsClient,
    private providerClient: ProviderClient,
    public dialog: MatDialog,
    private mobileObserverService: MobileObserverService,
    private drawerService: DrawerService,
    private userWorkspaceService: UserWorkspaceService,
    private tempFileClient: TempFileClient,
    private permissionNavTabHelper: PermissionNavTabHelper
  ) {}

  async ngOnInit(): Promise<void> {
    this.useMobileViewSubscription = this.mobileObserverService
      .mobileObserver()
      .subscribe(isMobile => (this.useMobileView = isMobile));
    this.useTabletViewSubscription = this.mobileObserverService
      .tabletObserver()
      .subscribe(isTablet => (this.useTabletView = isTablet));

    this.organizationId =
      this.userWorkspaceService.currentOrganization?.organizationId;

    this.searchOrders = this.displayedColumns
      .filter(el => el !== 'actions')
      .map(key => ({
        id: this.formatMailAccountSearchOrders.transform(key),
        name: key,
      }));

    await this.loadCanManageMailAccounts();
    await this.loadMailAccounts(true);
  }

  private async loadCanManageMailAccounts(): Promise<void> {
    this.organizationId &&
      (this.canManageMailAccounts =
        await this.permissionService.hasPermissionOver(
          this.organizationId,
          null,
          PermissionType.CanManageMailAccounts
        ));

    this.canManageMailAccount =
      await this.permissionService.hasPermissionInMailAccounts(
        PermissionType.CanManageMailAccount
      );
  }

  clearSort(): void {
    if (!this.sort) {
      return;
    }
    this.sort.sort({ id: '', start: 'asc', disableClear: false });
    this.sort.active = '';
    this.sort.direction = '';
  }

  async reload(): Promise<void> {
    this.isReloadLoading = true;
    this.filter = { ...defaultFilterFields };
    this.searchOrder = 0;
    this.suppressSortDrivenSearch = true;
    this.clearSort();
    try {
      await this.onSearch();
    } finally {
      this.isReloadLoading = false;
      setTimeout(() => {
        this.suppressSortDrivenSearch = false;
      }, 0);
    }
  }

  async changeOrder(): Promise<void> {
    this.filter.orderDirection =
      this.filter.orderDirection === OrderDirection.Ascending
        ? OrderDirection.Descending
        : OrderDirection.Ascending;
    await this.onSearch();
  }

  resetScrollData(): void {
    this.offset = 0;
  }

  async onSearch(fetchSearchCounter = true): Promise<void> {
    this.resetScrollData();
    this.infinityScroll?.scrollTop();
    await this.loadMailAccounts(fetchSearchCounter);
  }

  public async loadMailAccounts(fetchSearchCounter: boolean): Promise<void> {
    if (!this.organizationId) return;
    this.canNavigateForward = false;
    const loaderTimeout =
      !this.offset && !this.dataSource.data.length
        ? setTimeout(() => {
            this.isLoading = true;
          }, 500)
        : undefined;
    this.offset && (this.isLoadingScroll = true);
    this.isError = false;
    this.isErrorScroll = false;

    try {
      const nullableSearchValue = this.searchValue ? this.searchValue : null;
      const organizationSettings =
        await this.organizationSettingsClient.getByOrganization(
          new BaseGetByIdRequest(this.organizationId)
        );
      this.iscredemISAMSupport = organizationSettings.result.credemISAMSupport;
      const request = {
        organizationId: this.organizationId,
        offset: this.offset,
        fetch: this.stepFetch,
        filter: this.filter,
        search: nullableSearchValue,
      };
      const response = await this.mailAccountClient.getSearch(request);
      const data = await Promise.all(
        response.data.map(async el => ({
          ...el,
          mailAccLink:
            await this.permissionNavTabHelper.getMailAccountPagesLink(
              el.mailAccountId,
              this.organizationId
            ),
          canManageMailAccountUsers: this.canManageMailAccounts
            ? true
            : await this.permissionService.hasPermissionOver(
                null,
                el.mailAccountId,
                PermissionType.CanManageMailAccountUsers
              ),
        }))
      );
      const result = {
        ...response,
        data,
      };

      this.spaceUsed = result.spaceUsed ?? 0;
      if (this.spaceUsed === undefined || this.spaceUsed === null) {
        this.spaceUsed = 0;
      } // if something goes wrong

      this.dataSource.data =
        this.offset === 0
          ? result.data
          : [...this.dataSource.data, ...result.data];

      if (fetchSearchCounter) {
        const counterRequest = new BaseGetSearchCountByIdRequest(
          this.organizationId,
          nullableSearchValue
        );
        const counterResponse =
          await this.mailAccountClient.getSearchCount(counterRequest);
        this.totalSize = counterResponse.result;
      }

      this.providers = (
        await this.providerClient.getByOrganization(
          new BaseGetByIdRequest(this.organizationId)
        )
      ).providerContracts.map(elem => ({
        val: elem.providerId,
        name: elem.name,
      }));
      this.canNavigateForward = this.totalSize !== this.dataSource.data.length;
    } catch (e) {
      if (this.isLoadingScroll) {
        this.isErrorScroll = true;
      } else {
        this.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.isLoading && (this.isLoading = false);
      this.isLoadingScroll && (this.isLoadingScroll = false);
    }
  }

  public onSearchValueChanged(newSearchValue: string): void {
    this.searchValue = newSearchValue;
  }

  public getDetailsLink(mailAccountId: number): string {
    return `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${GENERAL}`;
  }

  async onCreateMailAccountBtnClicked(): Promise<void> {
    this.drawerService.openDrawer(this.createDrawer);
  }

  public async searchOrderChange($event: any): Promise<void> {
    this.searchOrder = $event as MailAccountSearchOrder;
    this.filter.searchOrder = this.searchOrder;
    this.filter.orderDirection = OrderDirection.Ascending;
    await this.onSearch();
  }

  public async searchProviderChange(provider: {
    val: number;
    name: string;
  }): Promise<void> {
    this.filter.ProviderId = provider.val;
    await this.onSearch();
  }

  public async setFilterChange(
    name: string,
    val: any,
    fieldRemove?: string
  ): Promise<void> {
    this.filter[name as keyof FilterFields<MailAccountSearchOrder>] = val;
    if (fieldRemove) {
      delete this.filter[
        fieldRemove as keyof FilterFields<MailAccountSearchOrder>
      ];
    }
    await this.onSearch();
  }

  async onCreateMailAccount(): Promise<void> {
    await this.onSearch();
  }

  public async redirectToDetails(link?: string): Promise<void> {
    link && (await this.router.navigateByUrl(link));
  }

  public onAddMailAccountToUsersBtnClicked(
    mailAccount: MailAccountGetByOrganizationIdContract
  ): void {
    this.selectedMailAccount = mailAccount;
    this.drawerService.openDrawer(this.addMailAccountsToUsersDrawer);
  }

  public async onHideAddMailAccountsToUsersForm(): Promise<void> {
    this.selectedMailAccount = null;
  }

  public async onScroll(): Promise<void> {
    this.offset = this.offset + this.pageSize;
    await this.loadMailAccounts(false);
  }

  async refreshData(): Promise<void> {
    await this.loadMailAccounts(false);
  }

  async sortData(sort: Sort): Promise<void> {
    if (this.suppressSortDrivenSearch) {
      return;
    }
    this.filter.searchOrder = sort.direction
      ? this.formatMailAccountSearchOrders.transform(sort.active)
      : MailAccountSearchOrder.OrderByMailAccountId;

    this.filter.orderDirection =
      sort.direction === 'desc'
        ? OrderDirection.Descending
        : OrderDirection.Ascending;

    await this.onSearch();
  }

  async exportExcel(): Promise<void> {
    if (!this.organizationId) return;
    this.isExporting = true;
    try {
      const fileResponse = await this.mailAccountClient.exportExcel();
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExporting = false;
    }
  }

  async retryData(): Promise<void> {
    await this.loadMailAccounts(true);
    setTimeout(() => {
      this.infinityScroll?.reconnect();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.useMobileViewSubscription?.unsubscribe();
    this.useTabletViewSubscription?.unsubscribe();
  }
}
